import React, { useEffect, useState } from "react";
import Papa from 'papaparse';
import dayjs from "dayjs";
import { useForm } from "antd/es/form/Form";
import { useNavigate, Link } from "react-router-dom";
import { Col, Form, Input, Row, Select, Button, Upload, message, DatePicker, Flex, Spin } from "antd";
import { useDispatch, useSelector } from "react-redux";
import { useAuth } from "../../store/AuthContext";
import { getPartners, addReport, clearData } from "./reportSlice";
import { PageHeader } from "../../components/PageHeader/PageHeader";
import { CSVLink } from "react-csv";
import { Mapper } from "./Mapper";
import { Preview } from "./Preview";
import { SectionHeader } from "../../components/SectionHeader/SectionHeader";
import { reportMappings } from "../../constants/reportMappings";
import classNames from "classnames";
import css from "./Report.module.css"; 

export const Report = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [form] = useForm();
  
  const [step, setStep] = useState(1);
  const [formValues, setFormValues] = useState(null);
  const [fields, setFields] = useState({});
  const [headers, setHeaders] = useState([]);
  const [rows, setRows] = useState([]);
  const { user } = useAuth(); 
  const { partners, getPartnersInProgress, addReportInProgress } =
	useSelector((state) => state?.report) || {};

  const onSubmit = async () => {  
	let mappedRows = [], 
		numericFields = ['stock', 'total_sales', 'seven_day_sales'];
	
	for(let row of rows) {
		let mappedRow = {
			partner: formValues.partner,
			report_date: formValues.report_date.format("YYYY-MM-DD")	
		};
		for(let field of Object.keys(fields)) {
			const value = row[fields[field]];
			mappedRow[field] = numericFields.includes(field) ? Number(value) : value.trim();
		}
		mappedRows.push(mappedRow);
	}
	
	const result = await dispatch(
		addReport({payload: mappedRows, file: formValues.fileName, user: user.email})
	);
	let from;
	if(result.error) {
		from = '/upload-error';
	} else {
		from = '/uploaded';
	}
	form.resetFields();
	navigate("/report/logs", { state: { from } });
  };
  
  const getStepClass = (currentStep) => {
	  //css.withError,
	let classes = [css.stepItem];  
	if(currentStep == step)
		classes.push(css.current);
	else if(currentStep < step) 
		classes.push(css.past);
	else
		classes.push(css.future);	
		
	return classNames(...classes);
  }
  
  const submitUploadForm = (formData) => {
	formData.fileName = formData.file.file.name;
	setFormValues(formData);
	setStep(2);
  }
  
  const submitFieldMap = (fields) => {
	setFields(fields);
	setStep(3);
  }
  
  const beforeUpload = (file) => {
	const isCsv = file.type === 'text/csv' || file.type === 'application/csv';
	if (!isCsv) {
	  message.error('You can only upload CSV file!');
	  return Upload.LIST_IGNORE;
	}
	if(isCsv) {
		return Papa.parse(file, {
			header: true,
			skipEmptyLines: true,
			complete: function(results) {
				if(results.data && results.data.length > 0) {
					const { data } = results;
					const row = data[0];
					setHeaders(Object.keys(row));
					setRows(data);
				}
			},
			error: function(error) {
				message.error('Error parsing CSV:' + error);
				return Upload.LIST_IGNORE;
			},
			transform: function(value, header) {
				value = value.trim();
				if(value.startsWith("'") && !value.endsWith("'")) {
					value = value.substr(1);
				}
				return value;
			  },
		});
	}
	return false;
  };
  
  const renderSampleDownload = () => {
	const sampleData = reportMappings.map(field => ({[field.value]:''}));
	return <CSVLink data={sampleData} filename="sample.csv">Download Sample</CSVLink>
  }
  
  useEffect(() => {
	dispatch(getPartners());
  }, []);

  const rules = [
	{
	  required: true,
	  message: "This field is required",
	},
  ];
  
  const submitReady = Object.keys(fields).length > 0;

  return (
	<>
	  <PageHeader title="Report Uploader" extraClass={css.reportTitle} />
		<Form
		form={form}
		name="importReportForm"
		autoComplete="off"
		layout="vertical"
		initialValues={{report_date: dayjs()}}
		onFinish={submitUploadForm}
		>
			<Flex className={css.root} vertical>
				<div className={"ibox-holder"}>
					<div className={css.stepsHolder}>
						<div className={getStepClass(1)}> 
							<span className={css.stepNumber}>1</span>
							<div className={css.stepDescription}>
								<h3 className={css.stepTitle}>Step 1: Choose .csv</h3>
								<p className={css.stepText}>Upload csv file from your computer. File must be in .csv format with headers in row 1, and with no total rows at the bottom</p>
							</div>
						</div>
						<div className={getStepClass(2)}>
							<span className={css.stepNumber}>2</span>
							<div className={css.stepDescription}>
								<h3 className={css.stepTitle}>Step 2: Match Headers</h3>
								<p className={css.stepText}>Match the required header columns from the file. The file should contain all the required fields marked with * plus a Sales figure, Stock figure, or both</p>
							</div>
						</div>
						<div className={getStepClass(3)}>
							<span className={css.stepNumber}>3</span>
							<div className={css.stepDescription}>
								<h3 className={css.stepTitle}>Step 3: Upload Your Files</h3>
								<p className={css.stepText}>Click Upload to load your files into the database. If you make a mistake click “View Logs” to delete and entry</p>
							</div>
						</div>
					</div>
					{step == 1 && <>
					<SectionHeader title="Choose CSV" />
					<Row gutter={16}>
						<Col xs={24} md={18} lg={12} xl={8} xxl={6}>
						<Form.Item
							name="partner"
							className={classNames(css.reportFormItem, css.inlineFormItem)}
							label={<span className={css.label}>Partner</span>}
							rules={rules}
						>
							<Select
								allowClear
								placeholder="Partner"
								// size="large"
								options={partners}
							/>
						</Form.Item>
						</Col>
					</Row>
					<Row gutter={16}>
						<Col xs={24} md={18} lg={12} xl={8} xxl={6}>
						<Form.Item
							name="report_date"
							className={classNames(css.reportFormItem, css.inlineFormItem)}
							label={<span className={css.label}>Report Date</span>}
							rules={rules}
							defaultValue={dayjs()}
							getValueProps={(i) => ({ value: dayjs(i) })}
						>
							<DatePicker
								format='YYYY-MM-DD'
								placeholder='YYYY-MM-DD'
								allowClear={false}
							/>
						</Form.Item>
						</Col>
					</Row>
					<Row gutter={16}>
						<Col xs={24} md={8}>
						<Form.Item
							name="file"
							className={classNames(css.reportFormItem, css.inlineFormItem, css.uploadCsv)}
							
							rules={rules}
						>
						
						<Upload
							name="csv"
							showUploadList={false}
							beforeUpload={beforeUpload}
							
							>
							<Button>Choose CSV File</Button>
							{renderSampleDownload()}
						</Upload>
						
						</Form.Item>
						</Col>
					</Row>
					<Row gutter={16}>
						<Col xs={24} md={8}>
							<Form.Item
								className={css.buttonsHolder}
							>
								<Button
									size="large"
									rootClassName={css.submitButton}
									htmlType="submit"
									loading={false}
									type="primary"
								>
									Next
								</Button> 
							</Form.Item>
						</Col>
					</Row>
					</>}
					{step == 3 && <>
					<SectionHeader title="Upload Your Files" />
					<Row gutter={16}>
						<Col xs={24} md={8}>
							<Form.Item
								className={css.buttonsHolder}
							>
								<Button
									size="large"
									rootClassName={css.submitButton}
									onClick={onSubmit}
									type="primary"
								>
									Upload
								</Button> 
								{addReportInProgress && <Spin size="small" />}
							</Form.Item>
						</Col>
					</Row>
					</>}
				</div>
				{step == 2 && <>
					<Mapper headers={headers} onSubmit={submitFieldMap} fileName={formValues.fileName} />
					<div className={"ibox-holder dark"}>
						<Preview headers={headers} rows={rows} />
					</div>
				</>}
			</Flex>
		</Form>
	</>
  );
  
};
